// phase-compiler-six-tabs — the RELEASE/QC half of the old Operations tab,
// relocated onto the six-tab workspace's Validation tab.
//
// The old NavigationReleaseOperations bundled two unrelated things: the
// submission navigation-policy SNAPSHOT (which unlocks byte-changing edits and
// now lives beside the editors on Bookmarks & Links) and the per-document
// release intake + publishing-QC SIGN-OFF (which is a validation gate and now
// lives on Validation). This wrapper renders ONLY the release half.
//
// NavigationReleaseControls binds intake to the SELECTED document's SOURCE
// bytes, so it needs the authoritative source hash. That hash comes from the
// navigation envelope (the server is authoritative; the client never invents
// it), so this component mounts its own envelope for the selected document.
// While the hash is unknown (loading / failed) it shows an HONEST state rather
// than binding intake to a guessed or empty hash.

import { type ReactElement } from 'react';
import { NavigationReleaseControls } from './NavigationReleaseControls';
import { useNavigationEnvelope } from './useNavigationEnvelope';
import { navStyles } from './navShared';

export interface NavigationReleaseReviewProps {
  readonly submissionId: string;
  readonly documentId: string;
}

export function NavigationReleaseReview({
  submissionId,
  documentId,
}: NavigationReleaseReviewProps): ReactElement {
  const { env, loading, loadError } = useNavigationEnvelope(documentId);

  return (
    <div style={{ marginTop: 12 }} data-testid="nav-release-review">
      {loading && !env ? (
        <div style={navStyles.muted} data-testid="nav-release-review-loading">
          Loading the document identity for release review…
        </div>
      ) : loadError && !env ? (
        <div style={navStyles.error} role="alert" data-testid="nav-release-review-error">
          Could not load the document identity for release review: {loadError}
        </div>
      ) : env ? (
        <NavigationReleaseControls
          submissionId={submissionId}
          documentId={documentId}
          sourceSha256={env.sourceSha256}
        />
      ) : (
        <div style={navStyles.muted} data-testid="nav-release-review-empty" />
      )}
    </div>
  );
}

export default NavigationReleaseReview;
